/**
 * Copy Trading. Limits, the Copy Stop Loss, sizing and the API shapes
 * shared by the API and the web app. Amounts are USDT strings.
 */
import { D } from './decimal.js';
import { clampLeverage, leverageCap } from './trading-math.js';
export const COPY_LIMITS = {
    /** Smallest amount to start a copy, and what must stay in it after removing funds. */
    minAmount: '200',
    maxAmount: '500000',
    maxRunningCopies: 100,
    /** Copied trades smaller than this (or the instrument minimum) are skipped. */
    minTrade: '1',
    defaultStopLossPct: '0.4',
    minStopLossPct: '0.05',
    maxStopLossPct: '0.95',
    /** Warn the copier when this share of the allowed loss is used up. */
    stopLossWarnAt: '0.75',
    /** Lead traders with this risk score or above take no new copiers. */
    noNewCopiersRisk: 9,
};
/** Who can apply to be a lead trader. */
export const LEAD_TRADER_REQUIREMENTS = {
    minEquity: '1000',
    minTradingDays: 30,
    maxRiskScore: 7,
};
/** The equity at which a copy closes: invested × (1 − stop loss %). */
export const stopLossLevel = (invested, pct) => D.from(invested).mul(D.ONE.sub(pct)).round(8, 'down');
/** A risk score from the 1-day 99 % value at risk of daily returns, as a fraction of equity. */
export function riskScoreFromVar(var99) {
    const v = D.from(var99).mul(100); // percent
    const bands = ['1', '2', '3', '5', '7', '10', '15', '25', '40'];
    let score = 1;
    for (const b of bands)
        if (v.gte(b))
            score++;
    return score;
}
/** Human wording for why a copied trade was not opened. */
export const COPY_SKIP_REASONS = {
    below_min: 'Too small to copy at your amount',
    insufficient_funds: 'Not enough money left in the copy',
    market_closed: 'Market was closed',
    paused: 'Copy was paused',
    already_copied: 'Already copied',
    stale: 'Price moved on before it could be copied',
    copy_restricted: 'Copying is restricted on your account',
    trading_restricted: 'Trading is restricted on your account',
    exposure_cap: 'Over the copy exposure limit',
    master_paused: 'The master account was paused',
    // Before 2026-10-20 evening a master's trade from chart skipped a follower who started after its entry.
    started_after: 'Copying started after this trade opened',
    /** Not a skip: a master's close from chart reached a copy opened after that minute, which closed at today's price. */
    closed_live: 'Copied after that time: closed at today’s price',
    rejected: 'Not accepted',
};
export function copyOpenSize(x) {
    const leaderEq = D.from(x.leaderEquity);
    if (!leaderEq.isPos())
        return { ratio: D.ZERO, leverage: x.leaderLeverage, notional: D.ZERO, cappedFrom: null, skip: 'rejected' };
    const ratio = D.from(x.copyEquity).div(leaderEq, 'down').mul(x.multiplier ?? 1, 'down');
    const cap = leverageCap(x.inst.maxLeverage, x.copierCap);
    const leverage = clampLeverage(x.leaderLeverage, { minLeverage: x.inst.minLeverage, maxLeverage: x.masterCap ? Math.min(cap, x.masterCap) : cap });
    const share = D.from(x.leaderMargin).mul(ratio, 'down').mul(leverage, 'down').round(2, 'down');
    if (share.lt(D.max(D.from(COPY_LIMITS.minTrade), D.from(x.inst.minNotional))))
        return { ratio, leverage, notional: share, cappedFrom: null, skip: 'below_min' };
    if (share.gt(x.inst.maxNotional))
        return { ratio, leverage, notional: D.from(x.inst.maxNotional), cappedFrom: share, skip: null };
    return { ratio, leverage, notional: share, cappedFrom: null, skip: null };
}
/** Public AUM band (shown as a range, never the exact amount). */
export function aumBand(aum) {
    const v = D.from(aum);
    const bands = [
        ['10000000', '$10M+'],
        ['1000000', '$1M–10M'],
        ['500000', '$500K–1M'],
        ['100000', '$100K–500K'],
        ['50000', '$50K–100K'],
        ['10000', '$10K–50K'],
    ];
    for (const [min, label] of bands)
        if (v.gte(min))
            return label;
    return '$0–10K';
}
// ---------- Master Accounts (dealer-run, staff-assigned) ----------
export const MASTER_LIMITS = {
    minReferenceEquity: '1000',
    maxReferenceEquity: '100000000',
    /** Per-client size multiplier on top of the equity ratio. */
    minMultiplier: '0.1',
    maxMultiplier: '5',
    /** A follower's stop loss goes up to 100 % (it closes only when its equity reaches 0), the default. */
    defaultStopLossPct: '1',
    maxStopLossPct: '1',
    /** Yearly management fee, as a fraction of equity (0.02 = 2 % a year). */
    maxMgmtFeePct: '0.05',
    /** Performance fee, as a fraction of the gain above the high-water mark. */
    maxPerfFeePct: '0.5',
};
/** The managed-account mandate version clients accept before anything is copied. */
export const MASTER_MANDATE_VERSION = '2026-10';
/**
 * The mandate's terms as the client reads them before accepting (version MASTER_MANDATE_VERSION).
 * DRAFT wording for legal review: change the version when the text changes.
 */
export const MASTER_MANDATE_TERMS = [
    'You authorise our dealing team to open and close trades for you with the funds you allocate to this managed account. You do not approve each trade.',
    'Every trade the dealer places on the master account is copied to your managed account, sized to its value compared with the master account. Your results will differ from the master because of price, timing, fees and trades too small to copy.',
    'Only the money moved into the managed account is traded. The rest of your live account is not affected.',
    'A stop loss is set on the managed account. If its value falls to that level, all its trades close and what is left returns to your live account.',
    'Trades are CFDs with leverage. You can lose the whole amount allocated. This is not investment advice and past results do not predict future results.',
    'You can ask to leave at any time from the managed account page. Our team then closes its trades and returns what is left to your live account.',
];
//# sourceMappingURL=copy.js.map